import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from '~/lib/stores/create-store';

/**
 * Audit log of the last 100 SSE events, NOT a data layer.
 *
 * Two distinct concerns share the SSE wire and it's worth keeping them
 * straight:
 *
 * 1. **Data layer — TanStack Query.** The actual source of truth for
 *    server data. `use-sse.ts` translates each SSE event into the
 *    appropriate `queryClient.invalidateQueries(...)` call; the affected
 *    hooks then refetch and components re-render off the new cache. If
 *    you want fresh server data, that path is what you want.
 *
 * 2. **Audit trail — this store.** A bounded ring buffer (last 100
 *    events) for the dev / debug inspector. Lets us answer "what just
 *    fired?" without opening DevTools' Network panel. Components should
 *    NOT read business data out of here — by the time a component
 *    needs the data, the query cache will already hold the post-event
 *    state. Reading from the event payload would re-implement the
 *    cache, badly, and miss server-side filtering.
 *
 * If you find yourself reaching for `useEventStore(...)` to render
 * domain state, you almost certainly want a TanStack Query hook
 * instead.
 */
export interface SSEvent {
  type: string;
  data: any;
  timestamp: number;
}

export interface EventState {
  events: SSEvent[];
  pushEvent: (event: SSEvent) => void;
  clear: () => void;
}

function createEventStore(): StoreApi<EventState> {
  return createStore<EventState>('event-store', (set) => ({
    events: [],
    pushEvent: (event) =>
      set((s) => ({ events: [event, ...s.events].slice(0, 100) })),
    clear: () => set({ events: [] }),
  }));
}

let store: StoreApi<EventState> | undefined;
export function getEventStore() {
  if (!store) store = createEventStore();
  return store;
}

if (import.meta.hot) {
  import.meta.hot.accept(() => { store = undefined; });
}
export function useEventStore<T>(selector: (state: EventState) => T): T {
  return useStore(getEventStore(), selector);
}
